/**
 * Outbound email provider card — pick Cloudflare / SES for the org.
 *
 * Credentials are not editable here: they are wrangler secrets on the API
 * deployment. The server reports only whether each provider's secrets are
 * present, and an option whose secrets are missing is disabled with the exact
 * `wrangler secret put` names to run. Only SES's non-secret region and
 * configuration set are per-org, and only fields the user edited are POSTed.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { emailProvider, type EmailProviderInput, type EmailProviderName } from "../../lib/api";
import { Button, Input } from "@/ui";

export const emailProviderQueryKey = ["email-provider", "config"] as const;

const LABEL: Record<EmailProviderName, string> = {
    cloudflare: "Cloudflare Email Service",
    ses: "Amazon SES",
};

/** What to run when a provider's secrets are missing. */
const SECRET_HINT: Record<EmailProviderName, string> = {
    cloudflare: "Not available on this deployment",
    ses: "Set AWS_ACCESS_KEY_ID / AWS_SECRET_ACCESS_KEY with wrangler secret put",
};

export function errorText(err: unknown): string {
    const body = (err as { body?: { error?: string } | string } | null)?.body;
    return String((typeof body === "object" ? body?.error : body) ?? (err as Error)?.message ?? "Something went wrong");
}

export default function EmailProviderSettings() {
    const queryClient = useQueryClient();
    const configQuery = useQuery({ queryKey: emailProviderQueryKey, queryFn: () => emailProvider.config() });
    const config = configQuery.data?.ok ? configQuery.data : null;

    const [picked, setPicked] = useState<EmailProviderName | null>(null);
    const [fields, setFields] = useState<Omit<EmailProviderInput, "provider">>({});
    const [testResult, setTestResult] = useState<string | null>(null);
    const stored = config?.provider === "resend" ? null : config?.provider ?? null; // legacy "resend" = unconfigured
    const provider = picked ?? stored;
    const set = (k: keyof typeof fields, v: string) => setFields((f) => ({ ...f, [k]: v }));

    /** Secrets for this option are on the deployment. */
    const available = (p: EmailProviderName): boolean =>
        p === "cloudflare" ? !!config?.cloudflareAvailable
            : !!config?.ses.secretsPresent;

    const save = useMutation({
        mutationFn: async () => {
            if (!provider) throw new Error("Pick a provider first");
            // Only fields the user actually touched are in `fields`, and only
            // those are sent: an untouched setting is omitted (server keeps it),
            // one emptied on purpose is sent as "" (server clears it).
            const edited = Object.fromEntries(
                Object.entries(fields).map(([k, v]) => [k, (v ?? "").trim()]),
            );
            const res = await emailProvider.set({ ...edited, provider });
            if (!res.ok) throw new Error(res.error);
            return res;
        },
        onSuccess: () => {
            setFields({});
            setTestResult(null);
            queryClient.invalidateQueries({ queryKey: emailProviderQueryKey });
        },
    });

    const test = useMutation({
        mutationFn: () => emailProvider.test(),
        onSuccess: (res) => setTestResult(res.ok ? `Sent via ${LABEL[res.provider]}` : res.error),
        onError: (err: unknown) => setTestResult(errorText(err)),
    });

    const clear = useMutation({
        mutationFn: () => emailProvider.clear(),
        onSuccess: () => {
            setPicked(null);
            setFields({});
            setTestResult(null);
            queryClient.invalidateQueries({ queryKey: emailProviderQueryKey });
        },
    });

    const status = stored
        ? `Sending via ${LABEL[stored]}${stored === "ses" && config?.ses.region ? ` (${config.ses.region})` : ""}`
        : "No provider configured — outbound email will fail until one is set.";

    return (
        <section className="settings-card">
            <h3>Email sending</h3>
            <p className="muted" style={{ fontSize: 13, lineHeight: 1.5 }}>
                Choose how outbound email is sent. Credentials are deployment secrets set with
                <code> wrangler secret put</code>, not stored here. Inbound mail routing is
                configured at deploy time and isn't affected.
            </p>

            <p style={{ fontSize: 13, marginTop: 8 }} className={stored ? undefined : "muted"}>
                {configQuery.isLoading ? "Loading…" : status}
            </p>

            {configQuery.isError && (
                <p className="danger" style={{ fontSize: 13, marginTop: 8 }}>{errorText(configQuery.error)}</p>
            )}

            {!config?.canEdit ? null : (
                <>
                    <div className="col" style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 12 }}>
                        {(["cloudflare", "ses"] as const).map((p) => {
                            const ok = available(p);
                            return (
                                <label key={p} className="row" style={{ gap: 8, alignItems: "center", opacity: ok ? 1 : 0.6 }}>
                                    <input
                                        type="radio"
                                        name="email-provider"
                                        checked={provider === p}
                                        disabled={!ok}
                                        onChange={() => setPicked(p)}
                                    />
                                    <span>{LABEL[p]}</span>
                                    <span className="muted" style={{ fontSize: 12 }}>
                                        {ok ? "Secrets present ✓" : SECRET_HINT[p]}
                                    </span>
                                </label>
                            );
                        })}
                    </div>

                    {provider === "ses" && (
                        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 12, maxWidth: 420 }}>
                            <Input
                                placeholder={config.ses.region ?? "Region (us-east-1)"}
                                value={fields.sesRegion ?? ""}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => set("sesRegion", e.target.value)}
                            />
                            <Input
                                placeholder={config.ses.configurationSet ?? "Configuration set (optional)"}
                                value={fields.sesConfigurationSet ?? ""}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => set("sesConfigurationSet", e.target.value)}
                            />
                            <p className="muted" style={{ fontSize: 12 }}>
                                Leave a field untouched to keep what is stored; clear one to remove it.
                            </p>
                        </div>
                    )}

                    <div className="row" style={{ gap: 8, marginTop: 12, alignItems: "center", flexWrap: "wrap" }}>
                        <Button variant="primary" disabled={!provider || save.isPending} onClick={() => save.mutate()}>
                            {save.isPending ? "Saving…" : "Save"}
                        </Button>
                        <Button
                            disabled={!stored || test.isPending}
                            title={stored ? undefined : "Save a provider first"}
                            onClick={() => { setTestResult(null); test.mutate(); }}
                        >
                            {test.isPending ? "Sending…" : "Send test email"}
                        </Button>
                        <Button variant="danger" disabled={!config.provider || clear.isPending} onClick={() => clear.mutate()}>
                            Clear
                        </Button>
                    </div>

                    {save.error && <p className="error" style={{ fontSize: 13, marginTop: 8 }}>{errorText(save.error)}</p>}
                    {testResult && <p className="muted" style={{ fontSize: 13, marginTop: 8 }}>{testResult}</p>}
                </>
            )}

            {/* A "Sending identities" card goes below this one — another engineer owns it. */}
        </section>
    );
}
